import { styled } from '@mui/material';
import { LoadingBar } from '../../components/LoadingBar/LoadingBar';
import { Outlet, useParams } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  activeQuestionIdState,
  activeReportState,
  activeSectionIdState,
  activeSurveyIdState,
  activeSurveyState,
  areShowedAllQuestionsState,
  isNameModalOpenState,
  responsesState,
  sectionsState,
  survayNameState,
} from '../../state/UIState';
import { REPORTS, useReportById, useUpdateReport } from '../../queries/useReports';
import { useQueryClient } from 'react-query';
import { REPORT_STATUS, SURVEY_TYPE } from '../../types';
import { SurveyEditorSkeletonLoader } from '../../components/SkeletonLoader/SurveyEditor.SkeletonLoader';
import { QuestionnaireHeader } from './components/PortalQuestionnaireHeader/QuestionnaireHeader';

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const LoadingBarWrapper = styled('div')`
  width: 100%;
  position: absolute;
  top: 0;
`;

const Content = styled('div')``;

export const Questionnaire = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [, setSurveyName] = useAtom(survayNameState);
  const params = useParams();

  const [, setOpen] = useAtom(isNameModalOpenState);
  const [, setActiveSurveyId] = useAtom(activeSurveyIdState);
  const [, setActiveReport] = useAtom(activeReportState);
  const [, setActiveSectionId] = useAtom(activeSectionIdState);
  const [, setActiveQuestionId] = useAtom(activeQuestionIdState);
  const [, setSections] = useAtom(sectionsState);
  const setAreShowedAllQuestions = useSetAtom(areShowedAllQuestionsState);
  const [, setResponses] = useAtom(responsesState);
  const [, setActiveSurvey] = useAtom(activeSurveyState);
  const updateReport = useUpdateReport();
  const queryClient = useQueryClient();
  const { data: report } = useReportById(Number(params?.reportId));

  const setReportInProgress = useCallback(async () => {
    if (!report || report?.status === REPORT_STATUS.IN_REVIEW) return;
    await updateReport.mutateAsync(
      { id: report.id, status: REPORT_STATUS.IN_PROGRESS },
      {
        onSuccess: () => {
          queryClient.refetchQueries(`${REPORTS}-${report.company.id}`);
          queryClient.refetchQueries(`${REPORTS}-${report.id}`);
        },
        onError: (e) => {
          console.error(e);
          setIsLoading(false);
        },
      }
    );
    setIsLoading(false);
    if (report?.response) setResponses(report.response);
  }, [queryClient, report, setResponses, updateReport]);

  useEffect(() => {
    if (!report) return;
    setActiveReport(report);
    const { survey } = report;
    setSurveyName(survey?.name ?? '');
    const sections =
      survey?.type === SURVEY_TYPE.CORE
        ? survey?.sections
            .filter((s) => s.questions.some((q) => q.isActive))
            .map((s) => ({ ...s, questions: s.questions.filter((q) => q.isActive) }))
        : survey?.sections;
    setSections(sections ?? []);
    setActiveSurveyId(survey?.id ?? null);
    setActiveSectionId(survey?.sections[0].id ?? null);
    setActiveQuestionId(survey?.sections[0].questions[0]?.id ?? null);
    setActiveSurvey(survey);

    setReportInProgress();

    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.id]);

  useEffect(() => {
    return () => {
      setActiveSurveyId(null);
      setActiveSectionId(null);
      setActiveQuestionId(null);
      setActiveSurvey(null);
      setSections([]);
      setResponses({});
      setAreShowedAllQuestions(true);
      setSurveyName('');
      setOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <SurveyEditorSkeletonLoader />;

  return (
    <Wrapper>
      <LoadingBarWrapper>
        <LoadingBar />
      </LoadingBarWrapper>
      <QuestionnaireHeader />
      <Content>
        <Outlet />
      </Content>
    </Wrapper>
  );
};
